import { useState } from "react";
import { toast } from "sonner";
import { GoogleLogo, Envelope, LockKey } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { formatApiErrorDetail } from "@/lib/format";
import { heroImg } from "@/lib/meta";

export default function Login() {
  const { loginPassword, registerPassword } = useAuth();
  const [tab, setTab] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      if (tab === "login") await loginPassword(email, password);
      else await registerPassword(email, password, name);
      toast.success("Signed in");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const inputCls = "rounded-sm bg-background border-border font-mono text-sm focus-visible:ring-primary";

  return (
    <div className="grain relative min-h-screen flex items-center justify-center px-4 py-10">
      <div className="absolute inset-0 bg-cover bg-center opacity-25" style={{ backgroundImage: `url(${heroImg})` }} />
      <div className="absolute inset-0 bg-background/85" />

      <div className="relative w-full max-w-4xl grid md:grid-cols-2 border border-border bg-card">
        {/* Brand side */}
        <div className="hidden md:flex flex-col justify-between p-10 border-r border-border bg-background/60">
          <div className="flex items-center gap-2.5">
            <img src="/logo.png" alt="Thermal Spray Atlas" className="h-9 w-9 rounded-sm object-cover" />
            <div className="leading-none">
              <div className="font-heading font-black text-white text-base tracking-tight">THERMAL SPRAY ATLAS</div>
              <div className="font-mono text-[10px] text-muted-foreground tracking-widest">AI COATING ADVISOR</div>
            </div>
          </div>
          <div>
            <h2 className="font-heading font-black text-white text-3xl leading-tight">
              The parameter atlas for <span className="text-primary">thermal spray</span> coatings.
            </h2>
            <p className="text-sm text-muted-foreground mt-4 leading-relaxed">
              Grounded recommendations across Arc, Flame, HVOF, HVOLF, Plasma, Cold Spray and SPS —
              for metals, ceramics, cermets, composites and MMCs.
            </p>
          </div>
          <div className="font-mono text-[10px] text-muted-foreground tracking-widest">SIGN IN TO CONTINUE</div>
        </div>

        {/* Form side */}
        <div className="p-8 sm:p-10">
          <h1 className="font-heading font-black text-white text-2xl">{tab === "login" ? "Welcome back" : "Create your account"}</h1>
          <p className="text-sm text-muted-foreground mt-2 mb-6">Access the AI coating advisor and materials database.</p>

          <div className="grid grid-cols-2 border border-border mb-5">
            <button
              data-testid="tab-login"
              onClick={() => { setTab("login"); setError(""); }}
              className={`py-2 text-xs font-mono uppercase tracking-wide transition-colors ${tab === "login" ? "bg-primary text-white" : "text-muted-foreground hover:text-white"}`}
            >Sign In</button>
            <button
              data-testid="tab-register"
              onClick={() => { setTab("register"); setError(""); }}
              className={`py-2 text-xs font-mono uppercase tracking-wide transition-colors ${tab === "register" ? "bg-primary text-white" : "text-muted-foreground hover:text-white"}`}
            >Register</button>
          </div>

          <form onSubmit={submit} className="space-y-3">
            {tab === "register" && (
              <div>
                <label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Name</label>
                <Input data-testid="auth-name" value={name} onChange={(e) => setName(e.target.value)} className={`mt-1.5 ${inputCls}`} />
              </div>
            )}
            <div>
              <label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1"><Envelope size={12} /> Email</label>
              <Input data-testid="auth-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={`mt-1.5 ${inputCls}`} />
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1"><LockKey size={12} /> Password</label>
              <Input data-testid="auth-password" type="password" required minLength={tab === "register" ? 8 : undefined} value={password} onChange={(e) => setPassword(e.target.value)} className={`mt-1.5 ${inputCls}`} />
            </div>
            {error && <div data-testid="auth-error" className="text-xs text-destructive border border-destructive/40 bg-destructive/10 px-3 py-2 rounded-sm">{error}</div>}
            <Button data-testid="auth-submit" type="submit" disabled={busy} className="w-full rounded-sm bg-primary hover:bg-primary/90 text-white font-mono uppercase tracking-widest text-xs h-11">
              {busy ? "..." : tab === "login" ? "Sign In" : "Create Account"}
            </Button>
          </form>

          <div className="flex items-center gap-3 my-5">
            <div className="h-px flex-1 bg-border" />
            <span className="font-mono text-[10px] uppercase text-muted-foreground">or</span>
            <div className="h-px flex-1 bg-border" />
          </div>

          <Button
            data-testid="google-login-btn"
            onClick={google}
            variant="outline"
            className="w-full rounded-sm bg-white hover:bg-white/90 text-black border-0 font-medium h-11"
          >
            <GoogleLogo size={18} weight="bold" className="mr-2" /> Continue with Google
          </Button>
        </div>
      </div>
    </div>
  );
}
